import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Stack,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Collapse,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountBalance as AccountIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Compare as CompareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { createToast, inferToastSeverityFromMessage, toastFromApiError, type AppToast } from '../utils/appToast';
import { ToastCodeCaption } from '../components/ToastCodeCaption';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface PandleTransaction {
  id: number;
  omar_id: string;
  account: string | null;
  entry_method: string | null;
  pandle_id: number | null;
  date: string;
  description: string | null;
  category: string | null;
  transaction_amount: number;
  transaction_amount_raw: string | null;
  transaction_type: string;
  vat_rate: string | null;
  month: string | null;
  year: number | null;
  year_month: string | null;
  potential_error: string | null;
  created_at: string;
  updated_at: string;
}

interface PandleStats {
  total_transactions: number;
  date_range: {
    min_date: string | null;
    max_date: string | null;
  };
  year_range: {
    min_year: number | null;
    max_year: number | null;
  };
  accounts: string[];
  categories: string[];
  totals: {
    credits: number;
    debits: number;
    net: number;
  };
  error_count: number;
}

interface MatchedPair {
  revolut_transaction: any;
  pandle_transaction: any;
  match_score: number;
  match_details: any;
}

interface MatchData {
  matched_pairs: MatchedPair[];
  unmatched_revolut: any[];
  unmatched_pandle: any[];
  summary: {
    total_revolut: number;
    total_pandle: number;
    confirmed_matches: number;
    matched_pairs: number;
    unmatched_revolut: number;
    unmatched_pandle: number;
    matching_enabled: boolean;
  };
  matching_enabled: boolean;
  timestamp: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pandle-tabpanel-${index}`}
      aria-labelledby={`pandle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `pandle-tab-${index}`,
    'aria-controls': `pandle-tabpanel-${index}`,
  };
}

const PandleTransactions: React.FC = () => {
  const { user } = useAuth();
  const canCsvImport = user?.tx_import === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<PandleTransaction[]>([]);
  const [stats, setStats] = useState<PandleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<AppToast | null>(null);

  const showImportSnackbar = (success: boolean, message: string) => {
    setImportResult(createToast(success ? 'success' : inferToastSeverityFromMessage(message), message));
  };

  // Tab names mapping
  const tabNames = ['transactions', 'matches'];
  const tabNameToIndex: { [key: string]: number } = {
    'transactions': 0,
    'matches': 1
  };
  
  // Determine initial tab from URL param
  let initialTab = 0;
  const tabParam = searchParams.get('tab');
  if (tabParam && tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
    initialTab = tabNameToIndex[tabParam.toLowerCase()];
  }
  
  const [tabValue, setTabValue] = useState(initialTab);
  
  // Match data state
  const [matchData, setMatchData] = useState<MatchData | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState({
    matched: true,
    unmatchedRevolut: true,
    unmatchedPandle: true
  });
  const [confirmedMatches, setConfirmedMatches] = useState<Set<string>>(new Set());
  const [confirmingMatch, setConfirmingMatch] = useState<string | null>(null);
  const [selectedMatches, setSelectedMatches] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPair, setSelectedPair] = useState<MatchedPair | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTransactionType, setSelectedTransactionType] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = {
        page: currentPage,
        per_page: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedAccount) params.account = selectedAccount;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedTransactionType) params.transaction_type = selectedTransactionType;
      if (selectedYear) params.year = selectedYear;
      
      const response = await api.get('/api/pandle-transactions/', { params });
      
      // Debug logging
      console.log('Pandle Transactions Response:', {
        status: response.status,
        dataKeys: Object.keys(response.data),
        transactionsCount: response.data.transactions?.length || 0,
        pagination: response.data.pagination,
        firstTransaction: response.data.transactions?.[0]
      });
      
      setTransactions(response.data.transactions || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch Pandle transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/api/pandle-transactions/stats');
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
    }
  };

  // Handle file import
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!canCsvImport) return;
    if (!selectedFile) {
      showImportSnackbar(false, 'Please select a file');
      return;
    }

    try {
      setImporting(true);
      setError('');
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Don't set Content-Type manually - axios will set it automatically with boundary
      // Use longer timeout for CSV imports (can take a while for large files)
      const response = await api.post('/api/pandle-transactions/import', formData, {
        timeout: 300000 // 5 minutes timeout for large CSV imports
      });
      
      showImportSnackbar(
        true,
        `Import complete! Imported: ${response.data.imported}, Updated: ${response.data.updated}, Errors: ${response.data.errors}`
      );
      
      setImportDialogOpen(false);
      setSelectedFile(null);
      
      // Refresh data
      fetchTransactions();
      fetchStats();
    } catch (err: any) {
      let errorMessage = 'Failed to import transactions';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Import timed out. The file may be too large. Please try again or split the file into smaller chunks.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Backend service is temporarily unavailable. Please try again in a few moments.';
      } else if (err.response?.status === 0 || err.message?.includes('CORS')) {
        errorMessage = 'Network error: Unable to connect to the server. Please check your connection and try again.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setImportResult(toastFromApiError(err, errorMessage));
      setError(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  // Initial load and when pagination/sorting changes
  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [currentPage, pageSize, sortBy, sortOrder]);

  // Also fetch on initial mount to ensure data loads
  useEffect(() => {
    if (transactions.length === 0 && !loading) {
      fetchTransactions();
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on initial mount

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
    fetchTransactions();
  }, [searchTerm, startDate, endDate, selectedAccount, selectedCategory, selectedTransactionType, selectedYear]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const handleDeleteAll = async () => {
    setDeleteAllDialogOpen(false);
    setDeletingAll(true);
    setError('');

    try {
      await api.delete('/api/pandle-transactions/all');
      // Refresh data after deletion
      fetchTransactions();
      fetchStats();
      showImportSnackbar(true, 'All transactions deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete all transactions');
      setImportResult(toastFromApiError(err, 'Failed to delete all transactions'));
      console.error('Error deleting all transactions:', err);
    } finally {
      setDeletingAll(false);
    }
  };

  // Fetch matches
  const fetchMatchesRef = React.useRef(false); // Track if fetch is in progress
  
  const fetchMatches = React.useCallback(async () => {
    // Prevent duplicate calls
    if (matchLoading || fetchMatchesRef.current) {
      return;
    }
    
    fetchMatchesRef.current = true;
    setMatchLoading(true);
    setMatchError('');

    try {
      const params: any = {
        limit_matches: 10  // Only fetch latest 10 matches for performance
      };
      
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/api/pandle-transactions/match-revolut', {
        timeout: 180000,  // Longer timeout for week-by-week matching
        params
      });
      
      setMatchData(response.data);
    } catch (err: any) {
      setMatchError(err.response?.data?.error || 'Failed to fetch matches');
      console.error('Error fetching matches:', err);
    } finally {
      setMatchLoading(false);
      fetchMatchesRef.current = false;
    }
  }, [startDate, endDate, matchLoading]); // Memoize based on date filters

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.7) return 'warning';
    return 'error';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with named tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabNames[newValue]);
    setSearchParams(newSearchParams, { replace: true });
    
    if (newValue === 1 && !matchData) {
      // Load matches when switching to matches tab (matching always enabled)
      fetchMatches();
    }
  };

  // Sync tab value with URL param (only when tab actually changes)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
      const newTabValue = tabNameToIndex[tabParam.toLowerCase()];
      if (newTabValue !== tabValue) {
        setTabValue(newTabValue);
        // Only fetch matches if switching to matches tab and we don't have data (matching always enabled)
        if (newTabValue === 1 && !matchData && !matchLoading) {
          fetchMatches();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('tab')]); // Only depend on tab param, not entire searchParams

  // Auto-fetch matches on initial page load if matches tab is active
  useEffect(() => {
    if (tabValue === 1 && !matchData && !matchLoading) {
      fetchMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on initial mount

  const handleConfirmMatch = async (pair: MatchedPair, index: number) => {
    const matchKey = `${pair.revolut_transaction.revolut_db_id}_${pair.pandle_transaction.pandle_id}`;
    
    if (confirmedMatches.has(matchKey)) {
      return; // Already confirmed
    }

    setConfirmingMatch(matchKey);

    try {
      const response = await api.post('/api/pandle-transactions/confirm-match', {
        revolut_transaction_id: pair.revolut_transaction.revolut_db_id,
        pandle_transaction_id: pair.pandle_transaction.pandle_id,
        match_score: pair.match_score
      });

      // Add to confirmed matches
      setConfirmedMatches(prev => new Set(prev).add(matchKey));
      
      // Show success message
      showImportSnackbar(true, 'Match confirmed successfully!');
    } catch (err: any) {
      setImportResult(toastFromApiError(err, 'Failed to confirm match'));
      console.error('Error confirming match:', err);
    } finally {
      setConfirmingMatch(null);
    }
  };

  const getMatchKey = (pair: MatchedPair) => {
    return `${pair.revolut_transaction.revolut_db_id}_${pair.pandle_transaction.pandle_id}`;
  };

  const isMatchConfirmed = (pair: MatchedPair) => {
    return confirmedMatches.has(getMatchKey(pair));
  };

  const handleSelectMatch = (index: number) => {
    setSelectedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSelectAllMatches = () => {
    if (!matchData) return;
    
    if (selectedMatches.size === matchData.matched_pairs.length) {
      // Deselect all
      setSelectedMatches(new Set());
    } else {
      // Select all
      const allIndices = matchData.matched_pairs.map((_, idx) => idx);
      setSelectedMatches(new Set(allIndices));
    }
  };

  const handleBulkConfirm = async () => {
    if (!matchData || selectedMatches.size === 0) return;

    setBulkActionLoading(true);
    const selectedPairs = Array.from(selectedMatches).map(idx => matchData.matched_pairs[idx]);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const pair of selectedPairs) {
        const matchKey = getMatchKey(pair);
        if (confirmedMatches.has(matchKey)) {
          continue; // Already confirmed
        }

        try {
          await api.post('/api/pandle-transactions/confirm-match', {
            revolut_transaction_id: pair.revolut_transaction.revolut_db_id,
            pandle_transaction_id: pair.pandle_transaction.pandle_id,
            match_score: pair.match_score
          });
          setConfirmedMatches(prev => new Set(prev).add(matchKey));
          successCount++;
        } catch (err: any) {
          errorCount++;
          console.error('Error confirming match:', err);
        }
      }

      setSelectedMatches(new Set());
      showImportSnackbar(
        errorCount === 0,
        `Confirmed ${successCount} match(es)${errorCount > 0 ? `. ${errorCount} failed.` : ''}`
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (!matchData || selectedMatches.size === 0) return;

    setBulkActionLoading(true);
    const selectedPairs = Array.from(selectedMatches).map(idx => matchData.matched_pairs[idx]);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const pair of selectedPairs) {
        const matchKey = getMatchKey(pair);
        
        try {
          await api.post('/api/pandle-transactions/reject-match', {
            revolut_transaction_id: pair.revolut_transaction.revolut_db_id,
            pandle_transaction_id: pair.pandle_transaction.pandle_id
          });
          successCount++;
        } catch (err: any) {
          errorCount++;
          console.error('Error rejecting match:', err);
        }
      }

      setSelectedMatches(new Set());
      // Refresh matches to remove rejected ones
      fetchMatches();
      showImportSnackbar(
        errorCount === 0,
        `Rejected ${successCount} match(es)${errorCount > 0 ? `. ${errorCount} failed.` : ''}`
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Pandle Transactions
        </Typography>
        <Box>
          {!canCsvImport && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, display: { xs: 'none', sm: 'inline' } }}>
              CSV import requires tx_import permission.
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => canCsvImport && setImportDialogOpen(true)}
            sx={{ mr: 1 }}
            disabled={!canCsvImport}
            title={
              !canCsvImport ? 'Ask an owner to grant the Revolut/Pandle ledger import capability (tx_import).' : undefined
            }
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteAllDialogOpen(true)}
            disabled={deletingAll || transactions.length === 0}
            sx={{ mr: 1 }}
          >
            Delete All
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchTransactions();
              fetchStats();
              if (tabValue === 1) {
                fetchMatches();
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="pandle transactions tabs"
            variant="fullWidth"
          >
            <Tab label="Transactions" {...a11yProps(0)} />
            <Tab label="Revolut Matches" {...a11yProps(1)} icon={<CompareIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>

          {/* Stats Cards */}
          {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Transactions
                </Typography>
                <Typography variant="h5">
                  {stats.total_transactions.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Credits
                </Typography>
                <Typography variant="h5" color="success.main">
                  {formatCurrency(stats.totals.credits)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Debits
                </Typography>
                <Typography variant="h5" color="error.main">
                  {formatCurrency(Math.abs(stats.totals.debits))}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Net Amount
                </Typography>
                <Typography variant="h5" color={stats.totals.net >= 0 ? 'success.main' : 'error.main'}>
                  {formatCurrency(stats.totals.net)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

          {/* Filters */}
          <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '180px' }}>
              <TextField
                fullWidth
                label="Search Description"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(15% - 16px)', minWidth: '140px' }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(15% - 16px)', minWidth: '140px' }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(15% - 16px)', minWidth: '140px' }}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  label="Account"
                >
                  <MenuItem value="">All</MenuItem>
                  {stats?.accounts.map((account) => (
                    <MenuItem key={account} value={account}>
                      {account}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(15% - 16px)', minWidth: '140px' }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  {stats?.categories.slice(0, 20).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(15% - 16px)', minWidth: '140px' }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={selectedTransactionType}
                  onChange={(e) => setSelectedTransactionType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Credit">Credit</MenuItem>
                  <MenuItem value="Debit">Debit</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </CardContent>
      </Card>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Transactions Table */}
          <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'date', pct: 10 },
                      { key: 'account', pct: 14 },
                      { key: 'description' },
                      { key: 'category', pct: 14 },
                      { key: 'amount', pct: 11 },
                      { key: 'type', pct: 9 },
                      { key: 'vat_rate', pct: 8 },
                      { key: 'pandle_id', pct: 10 },
                      ...(stats && stats.error_count > 0 ? [{ key: 'error', pct: 12 }] : []),
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Date</strong>
                        <span
                          onClick={() => handleSort('date')}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                        >
                          {sortBy === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </TableCell>
                      <TableCell><strong>Account</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell>
                        <strong>Amount</strong>
                        <span
                          onClick={() => handleSort('transaction_amount')}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                        >
                          {sortBy === 'transaction_amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>VAT Rate</strong></TableCell>
                      <TableCell><strong>Pandle ID</strong></TableCell>
                      {stats && stats.error_count > 0 && (
                        <TableCell><strong>Error</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={stats && stats.error_count > 0 ? 9 : 8} align="center">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((transaction) => (
                        <TableRow key={transaction.id} hover>
                          <TableCell>{formatDate(transaction.date)}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={transaction.account || ''}>{transaction.account || '-'}</Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={transaction.description || ''}>
                              {transaction.description || '-'}
                              {transaction.potential_error && (
                                <Chip
                                  icon={<ErrorIcon />}
                                  label="Error"
                                  color="error"
                                  size="small"
                                  sx={{ ...fitChipSx, ml: 1 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={transaction.category || ''}>{transaction.category || '-'}</Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color={transaction.transaction_type === 'Credit' ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                              sx={fitTruncateSx}
                            >
                              {formatCurrency(transaction.transaction_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.transaction_type}
                              color={transaction.transaction_type === 'Credit' ? 'success' : 'error'}
                              size="small"
                              sx={fitChipSx}
                            />
                          </TableCell>
                          <TableCell>{transaction.vat_rate || '-'}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={String(transaction.pandle_id || '')}>{transaction.pandle_id || '-'}</Box>
                          </TableCell>
                          {stats && stats.error_count > 0 && (
                            <TableCell>
                              {transaction.potential_error ? (
                                <Chip
                                  label={transaction.potential_error}
                                  color="error"
                                  size="small"
                                  sx={fitChipSx}
                                  title={String(transaction.potential_error)}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography>
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} transactions
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onClose={() => !importing && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Pandle Transactions CSV</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ marginTop: '16px', marginBottom: '16px' }}
            disabled={importing}
          />
          {importing && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Importing transactions...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} variant="contained" disabled={!selectedFile || importing}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

          {/* Import Result Snackbar */}
          <Snackbar
            open={!!importResult}
            autoHideDuration={6000}
            onClose={() => setImportResult(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity={importResult?.severity || 'info'} onClose={() => setImportResult(null)}>
              <Stack spacing={0.25}>
                <Typography variant="body2">{importResult?.message}</Typography>
                {importResult ? (
                  <ToastCodeCaption
                    code={importResult.diagnostics.code}
                    requestId={importResult.diagnostics.requestId}
                    jobId={importResult.diagnostics.jobId}
                    errorCode={importResult.diagnostics.errorCode}
                    copyBody={importResult.message}
                  />
                ) : null}
              </Stack>
            </Alert>
          </Snackbar>

          {/* Delete All Confirmation Dialog */}
          <Dialog open={deleteAllDialogOpen} onClose={() => !deletingAll && setDeleteAllDialogOpen(false)}>
            <DialogTitle>Delete All Transactions</DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone!
              </Alert>
              <Typography>
                Are you sure you want to delete all {totalCount.toLocaleString()} Pandle transactions?
                This will permanently remove all transaction data from the database.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteAllDialogOpen(false)} disabled={deletingAll}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteAll} 
                color="error" 
                variant="contained"
                disabled={deletingAll}
                startIcon={deletingAll ? <CircularProgress size={20} /> : <DeleteIcon />}
              >
                {deletingAll ? 'Deleting...' : 'Delete All'}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Matches Tab Content */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Revolut Transaction Matches
              </Typography>
              <Box display="flex" gap={1}>
                {selectedMatches.size > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleBulkConfirm}
                      disabled={bulkActionLoading}
                    >
                      Confirm Link ({selectedMatches.size})
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleBulkReject}
                      disabled={bulkActionLoading}
                    >
                      Reject Link ({selectedMatches.size})
                    </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    fetchMatches();
                    setSelectedMatches(new Set());
                  }}
                  disabled={matchLoading}
                >
                  Refresh Matches
                </Button>
              </Box>
            </Box>

            {matchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {matchError}
              </Alert>
            )}

            {matchLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : matchData ? (
              <>
                {/* Summary Cards */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <Card sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '180px' }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Revolut
                      </Typography>
                      <Typography variant="h5">
                        {matchData.summary.total_revolut.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '180px' }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Pandle
                      </Typography>
                      <Typography variant="h5">
                        {matchData.summary.total_pandle.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '180px' }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Confirmed Matches
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {matchData.summary.confirmed_matches?.toLocaleString() || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '180px' }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Matched Pairs
                      </Typography>
                      <Typography variant="h5" color="info.main">
                        {matchData.summary.matched_pairs.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '180px' }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Unmatched Revolut
                      </Typography>
                      <Typography variant="h5" color="warning.main">
                        {matchData.summary.unmatched_revolut.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* Matched Pairs Section */}
                <Card sx={{ mb: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 2,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleSection('matched')}
                  >
                    <Typography variant="h6">
                      Matched Pairs ({matchData.matched_pairs.length})
                    </Typography>
                    {expandedSections.matched ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={expandedSections.matched}>
                    <CardContent>
                      <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup
                            cols={[
                              { key: 'select', pct: 4 },
                              { key: 'match_score', pct: 8 },
                              { key: 'revolut_date', pct: 9 },
                              { key: 'revolut_description' },
                              { key: 'revolut_amount', pct: 10 },
                              { key: 'pandle_date', pct: 9 },
                              { key: 'pandle_description', pct: 16 },
                              { key: 'pandle_amount', pct: 10 },
                              { key: 'actions', pct: 18 },
                            ]}
                          />
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={matchData.matched_pairs.length > 0 && selectedMatches.size === matchData.matched_pairs.length}
                                  indeterminate={selectedMatches.size > 0 && selectedMatches.size < matchData.matched_pairs.length}
                                  onChange={handleSelectAllMatches}
                                />
                              </TableCell>
                              <TableCell><strong>Match Score</strong></TableCell>
                              <TableCell><strong>Revolut Date</strong></TableCell>
                              <TableCell><strong>Revolut Description</strong></TableCell>
                              <TableCell><strong>Revolut Amount</strong></TableCell>
                              <TableCell><strong>Pandle Date</strong></TableCell>
                              <TableCell><strong>Pandle Description</strong></TableCell>
                              <TableCell><strong>Pandle Amount</strong></TableCell>
                              <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matchData.matched_pairs.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={9} align="center">
                                  {matchData.summary.total_revolut === 0 ? (
                                    <Box>
                                      <Typography variant="body1" color="text.secondary" gutterBottom>
                                        No Revolut transactions found
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Please sync Revolut transactions first from the Revolut page
                                      </Typography>
                                    </Box>
                                  ) : (
                                    'No matches found'
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : (
                              matchData.matched_pairs.map((pair, idx) => {
                                const matchKey = getMatchKey(pair);
                                const isConfirmed = isMatchConfirmed(pair);
                                const isConfirming = confirmingMatch === matchKey;
                                const isSelected = selectedMatches.has(idx);
                                
                                return (
                                  <TableRow key={idx} hover selected={isSelected}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() => handleSelectMatch(idx)}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={`${(pair.match_score * 100).toFixed(1)}%`}
                                        color={getMatchScoreColor(pair.match_score) as any}
                                        size="small"
                                        sx={fitChipSx}
                                      />
                                    </TableCell>
                                    <TableCell>{formatDate(pair.revolut_transaction.date)}</TableCell>
                                    <TableCell>
                                      <Box component="span" sx={fitTruncateSx} title={pair.revolut_transaction.description || ''}>
                                        {pair.revolut_transaction.description || '-'}
                                      </Box>
                                    </TableCell>
                                    <TableCell>{formatCurrency(pair.revolut_transaction.amount)}</TableCell>
                                    <TableCell>{formatDate(pair.pandle_transaction.date)}</TableCell>
                                    <TableCell>
                                      <Box component="span" sx={fitTruncateSx} title={pair.pandle_transaction.description || ''}>
                                        {pair.pandle_transaction.description || '-'}
                                      </Box>
                                    </TableCell>
                                    <TableCell>{formatCurrency(pair.pandle_transaction.amount)}</TableCell>
                                    <TableCell align="right" className="fit-table-no-clip">
                                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                        <Tooltip title="View Details">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              setSelectedPair(pair);
                                              setDetailsModalOpen(true);
                                            }}
                                          >
                                            <InfoIcon />
                                          </IconButton>
                                        </Tooltip>
                                        {isConfirmed ? (
                                          <Chip
                                            icon={<CheckCircleIcon />}
                                            label="Confirmed"
                                            color="success"
                                            size="small"
                                          />
                                        ) : (
                                          <Tooltip title="Confirm Match">
                                            <Button
                                              variant="contained"
                                              color="primary"
                                              size="small"
                                              startIcon={<CheckCircleIcon />}
                                              onClick={() => handleConfirmMatch(pair, idx)}
                                              disabled={isConfirming}
                                            >
                                              {isConfirming ? 'Confirming...' : 'Confirm'}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Collapse>
                </Card>

                {/* Unmatched Revolut Section */}
                <Card sx={{ mb: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 2,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleSection('unmatchedRevolut')}
                  >
                    <Typography variant="h6">
                      Unmatched Revolut ({matchData.unmatched_revolut.length})
                    </Typography>
                    {expandedSections.unmatchedRevolut ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={expandedSections.unmatchedRevolut}>
                    <CardContent>
                      <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup
                            cols={[
                              { key: 'date', pct: 16 },
                              { key: 'description' },
                              { key: 'amount', pct: 16 },
                            ]}
                          />
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell><strong>Amount</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matchData.unmatched_revolut.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  {matchData.summary.total_revolut === 0 ? (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">
                                        No Revolut transactions to match
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Sync Revolut transactions from the Revolut page first
                                      </Typography>
                                    </Box>
                                  ) : (
                                    'All Revolut transactions matched'
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : (
                              matchData.unmatched_revolut.slice(0, 50).map((tx, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell>{formatDate(tx.date)}</TableCell>
                                  <TableCell>
                                    <Box component="span" sx={fitTruncateSx} title={tx.description || ''}>{tx.description || '-'}</Box>
                                  </TableCell>
                                  <TableCell>{formatCurrency(tx.amount)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Collapse>
                </Card>

                {/* Unmatched Pandle Section */}
                <Card>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 2,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleSection('unmatchedPandle')}
                  >
                    <Typography variant="h6">
                      Unmatched Pandle ({matchData.unmatched_pandle.length})
                    </Typography>
                    {expandedSections.unmatchedPandle ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={expandedSections.unmatchedPandle}>
                    <CardContent>
                      <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup
                            cols={[
                              { key: 'date', pct: 16 },
                              { key: 'description' },
                              { key: 'amount', pct: 16 },
                            ]}
                          />
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell><strong>Amount</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matchData.unmatched_pandle.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  {matchData.summary.total_revolut === 0 ? (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">
                                        No Revolut transactions to match against
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Sync Revolut transactions from the Revolut page first
                                      </Typography>
                                    </Box>
                                  ) : (
                                    'All Pandle transactions matched'
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : (
                              matchData.unmatched_pandle.slice(0, 50).map((tx, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell>{formatDate(tx.date)}</TableCell>
                                  <TableCell>
                                    <Box component="span" sx={fitTruncateSx} title={tx.description || ''}>{tx.description || '-'}</Box>
                                  </TableCell>
                                  <TableCell>{formatCurrency(tx.amount)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Collapse>
                </Card>
              </>
            ) : (
              <Alert severity="info">
                Click "Refresh Matches" to load transaction matches
              </Alert>
            )}
          </Box>
        </TabPanel>
      </Card>

      {/* Transaction Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Transaction Match Details
          {selectedPair && (
            <Chip
              label={`Match Score: ${(selectedPair.match_score * 100).toFixed(1)}%`}
              color={getMatchScoreColor(selectedPair.match_score) as any}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPair && (
            <TableContainer component={Paper} sx={{ ...FIT_TABLE_CONTAINER_SX, mt: 2 }}>
              <Table size="small" sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'field', pct: 22 },
                    { key: 'revolut' },
                    { key: 'pandle', pct: 39 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Field</strong></TableCell>
                    <TableCell><strong>Revolut Transaction</strong></TableCell>
                    <TableCell><strong>Pandle Transaction</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell>{formatDate(selectedPair.revolut_transaction.date)}</TableCell>
                    <TableCell>{formatDate(selectedPair.pandle_transaction.date)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell>
                      <Box component="span" sx={fitTruncateSx} title={selectedPair.revolut_transaction.description || ''}>
                        {selectedPair.revolut_transaction.description || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={fitTruncateSx} title={selectedPair.pandle_transaction.description || ''}>
                        {selectedPair.pandle_transaction.description || '-'}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell>{formatCurrency(selectedPair.revolut_transaction.amount)}</TableCell>
                    <TableCell>{formatCurrency(selectedPair.pandle_transaction.amount)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Currency</strong></TableCell>
                    <TableCell>{selectedPair.revolut_transaction.currency || 'EUR'}</TableCell>
                    <TableCell>{selectedPair.pandle_transaction.currency || 'EUR'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell>{selectedPair.revolut_transaction.type || '-'}</TableCell>
                    <TableCell>{selectedPair.pandle_transaction.type || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell>{selectedPair.revolut_transaction.id || selectedPair.revolut_transaction.revolut_id || '-'}</TableCell>
                    <TableCell>{selectedPair.pandle_transaction.id || selectedPair.pandle_transaction.omar_id || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Database ID</strong></TableCell>
                    <TableCell>{selectedPair.revolut_transaction.revolut_db_id || '-'}</TableCell>
                    <TableCell>{selectedPair.pandle_transaction.pandle_id || '-'}</TableCell>
                  </TableRow>
                  {selectedPair.revolut_transaction.source && (
                    <TableRow>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell>{selectedPair.revolut_transaction.source}</TableCell>
                      <TableCell>{selectedPair.pandle_transaction.source || '-'}</TableCell>
                    </TableRow>
                  )}
                  {selectedPair.pandle_transaction.account && (
                    <TableRow>
                      <TableCell><strong>Account</strong></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{selectedPair.pandle_transaction.account}</TableCell>
                    </TableRow>
                  )}
                  {selectedPair.pandle_transaction.category && (
                    <TableRow>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{selectedPair.pandle_transaction.category}</TableCell>
                    </TableRow>
                  )}
                  {selectedPair.pandle_transaction.vat_rate && (
                    <TableRow>
                      <TableCell><strong>VAT Rate</strong></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{selectedPair.pandle_transaction.vat_rate}</TableCell>
                    </TableRow>
                  )}
                  {selectedPair.match_details && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                          Match Details:
                        </Typography>
                        <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: '200px' }}>
                          {JSON.stringify(selectedPair.match_details, null, 2)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Show all other fields */}
                  {Object.keys(selectedPair.revolut_transaction).map((key) => {
                    if (['date', 'description', 'amount', 'currency', 'type', 'id', 'revolut_id', 'revolut_db_id', 'source'].includes(key)) {
                      return null;
                    }
                    return (
                      <TableRow key={`revolut-${key}`}>
                        <TableCell><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                        <TableCell>
                          {typeof selectedPair.revolut_transaction[key] === 'object' 
                            ? JSON.stringify(selectedPair.revolut_transaction[key])
                            : String(selectedPair.revolut_transaction[key] || '-')}
                        </TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    );
                  })}
                  {Object.keys(selectedPair.pandle_transaction).map((key) => {
                    if (['date', 'description', 'amount', 'currency', 'type', 'id', 'omar_id', 'pandle_id', 'source', 'account', 'category', 'vat_rate'].includes(key)) {
                      return null;
                    }
                    return (
                      <TableRow key={`pandle-${key}`}>
                        <TableCell><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          {typeof selectedPair.pandle_transaction[key] === 'object' 
                            ? JSON.stringify(selectedPair.pandle_transaction[key])
                            : String(selectedPair.pandle_transaction[key] || '-')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PandleTransactions;

